import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface AdminStatus {
  isAdmin: boolean;
  loading: boolean;
  adminProfile: {
    fullName: string | null;
  } | null;
}

/**
 * Single source of truth for "is the current user an admin?".
 * Cached through React Query so the many components that need it
 * (navigation, guards, dashboards, admin panels) share one request.
 */
export const useAdminRole = (): AdminStatus => {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-role', user?.id ?? null],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data: hasAdmin, error } = await supabase.rpc('has_role', {
        _user_id: user!.id,
        _role: 'admin',
      });

      if (error) throw error;
      if (!hasAdmin) return { isAdmin: false, adminProfile: null };

      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user!.id)
        .maybeSingle();

      return {
        isAdmin: true,
        adminProfile: { fullName: profileData?.full_name ?? null },
      };
    },
  });

  return {
    isAdmin: data?.isAdmin ?? false,
    loading: !!user && isLoading,
    adminProfile: data?.adminProfile ?? null,
  };
};
